import { api } from "../../../services/api";
import type { IngestJobResponse } from "../../../services/content-ingestion/content-ingestion.types";
import type {
  CourseQuizUpload,
  CourseQuizUploadResult,
  DocCheckInput,
  DocCheckResponse,
  ExamEvaluations,
  HubIngestionInput,
  HubLock,
  HubLockRequest,
  HubRowsResponse,
  PatchRowBody,
  UploadCheck,
  YamlFileResponse,
} from "./hubTypes";

// Unlike most services here, these let the error through: the runner turns
// the server's message into the text written to the tracker.

// `includeUploaded` also brings the rows whose parts are all ticked, for
// «Volver a subir lo ya subido».
export const getHubRows = async (
  spreadsheet: string,
  sheet: string,
  includeUploaded = false,
): Promise<HubRowsResponse> => {
  const response = await api.get("/content-hub/rows", {
    params: {
      spreadsheet,
      sheet,
      ...(includeUploaded && { includeUploaded: true }),
    },
  });
  return response.data;
};

export const checkDoc = async (
  input: DocCheckInput,
): Promise<DocCheckResponse> => {
  const response = await api.post("/content-hub/doc-check", input);
  return response.data;
};

// The only start call that falls back to the tracker's course when the doc
// declares none; the Subidor's keeps failing without a "Curso #".
export const startHubIngestion = async (
  input: HubIngestionInput,
): Promise<IngestJobResponse> => {
  const response = await api.post("/content-hub/ingestions", input);
  return response.data;
};

// What the platform holds for a course before uploading over it.
export const getUploadCheck = async (
  courseId: number,
): Promise<UploadCheck> => {
  const response = await api.get(
    `/content-hub/courses/${courseId}/upload-check`,
  );
  return response.data;
};

// Replaces the course's quiz in place (same IDs) or creates it, in one call.
export const getExamEvaluations = async (
  courseIds: number[],
): Promise<ExamEvaluations> => {
  const response = await api.get("/content-hub/courses/exam-evaluations", {
    params: { courseIds: courseIds.join(",") },
  });
  return response.data;
};

export const uploadCourseQuiz = async (
  courseId: number,
  body: CourseQuizUpload,
): Promise<CourseQuizUploadResult> => {
  const response = await api.post(
    `/content-hub/courses/${courseId}/quizzes`,
    body,
  );
  return response.data;
};

export const getYamlFile = async (
  fileId: string,
): Promise<YamlFileResponse> => {
  const response = await api.get(
    `/content-hub/yaml/${encodeURIComponent(fileId)}`,
  );
  return response.data;
};

export const patchHubRow = async (
  rowNumber: number,
  body: PatchRowBody,
): Promise<void> => {
  await api.patch(`/content-hub/rows/${rowNumber}`, body);
};

// A 409 says someone else holds some of what was asked for, or, when renewing,
// that this lock was lost.
export const acquireHubLock = async (
  request: HubLockRequest,
): Promise<HubLock> => {
  const response = await api.post("/content-hub/locks", request);
  return response.data;
};

export const renewHubLock = async (token: string): Promise<void> => {
  await api.patch(`/content-hub/locks/${encodeURIComponent(token)}`);
};

export const releaseHubLock = async (token: string): Promise<void> => {
  await api.delete(`/content-hub/locks/${encodeURIComponent(token)}`);
};
